/**
 * Cross-conversion between grid systems via lat/long pivot.
 *
 * convert(code, from, to, options) — translate any code into any other system.
 *
 * Note: cells in different systems are different sizes, so conversion is approximate
 * (we use the cell centre as the canonical lat/long for the source).
 */

import type { LatLng, GridSystem } from "./index.js";
import {
  encodeDigipin,
  decodeDigipin,
  type DigipinPrecision,
} from "./digipin.js";
import {
  encodePluscode,
  decodePluscode,
  type PluscodeLength,
} from "./pluscode.js";
import { encodeGeohash, decodeGeohash } from "./geohash.js";

export type ConvertOptions = {
  /** Target precision (DIGIPIN: 4-10, Plus: 4-12, GeoHash: 1-12). Defaults to system standard. */
  readonly precision?: number;
};

/**
 * Decode any code into a lat/long centre + the source system identifier.
 */
export function decodeAny(code: string, system: GridSystem): LatLng {
  switch (system) {
    case "digipin":
      return decodeDigipin(code).center;
    case "pluscode":
      return decodePluscode(code).center;
    case "geohash":
      return decodeGeohash(code).center;
  }
}

/**
 * Encode a lat/long into any system at given precision.
 */
export function encodeAny(
  point: LatLng,
  system: GridSystem,
  precision?: number,
): string {
  switch (system) {
    case "digipin":
      return encodeDigipin(point.lat, point.lng, (precision ?? 10) as DigipinPrecision);
    case "pluscode":
      return encodePluscode(point.lat, point.lng, (precision ?? 10) as PluscodeLength);
    case "geohash":
      return encodeGeohash(point.lat, point.lng, precision ?? 9);
  }
}

/**
 * Convert a code from one grid system to another, via lat/long pivot.
 *
 * Limitations:
 *  - Cell sizes differ across systems → conversion is centre-of-cell approximate
 *  - For DIGIPIN target, source must be inside India bounds, else throws
 */
export function convert(
  code: string,
  from: GridSystem,
  to: GridSystem,
  options: ConvertOptions = {},
): string {
  if (from === to) return code;
  const point = decodeAny(code, from);
  return encodeAny(point, to, options.precision);
}
